"use strict";
// Copyright © Veeam Software Group GmbH
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    Object.defineProperty(o, k2, { enumerable: true, get: function() { return m[k]; } });
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.PolicyPriorityOrderDialog = void 0;
const react_1 = __importStar(require("react"));
const lodash_maxby_1 = __importDefault(require("lodash.maxby"));
const lodash_isequal_1 = __importDefault(require("lodash.isequal"));
const components_1 = require("@veeam/components");
const core_1 = require("../../../core");
const helpers_1 = require("../../../helpers");
const components_2 = require("../../../components");
const components_3 = require("./components");
const helpers_2 = require("./helpers");
const types_1 = require("./types");
const ID_KEY = 'id';
const PolicyPriorityOrderDialog = ({ deactivate, onDone, mapper, proxies, }) => {
    var _a, _b;
    const { t, getProxy } = core_1.useFeatureContext();
    const { getConfig } = core_1.useAppContext();
    const spec = {
        filters: {
            name: '',
        },
        orderAsc: true,
        orderColumn: 'priority',
        skip: 0,
        count: getConfig().pageLimit,
    };
    const [selected, setSelected] = react_1.useState([]);
    const [currentData, setCurrentData] = react_1.useState([]);
    const [initialData, updateInitialData] = react_1.useState([]);
    const getPolicesProxyRef = react_1.useRef((proxies === null || proxies === void 0 ? void 0 : proxies.getPoliciesProxy) || getProxy('getPolices'));
    const sendChangesProxyRef = react_1.useRef((proxies === null || proxies === void 0 ? void 0 : proxies.changePriorityProxy) || getProxy('changePriority'));
    const [getPolicesResult, getPolicesActions] = core_1.useRequest({
        httpProxy: getPolicesProxyRef.current,
        payload: {},
        mapper,
    });
    const [sendChangesResult, sendChangesActions] = core_1.useRequest({
        httpProxy: sendChangesProxyRef.current,
        payload: {},
    });
    react_1.useEffect(() => {
        getPolicesActions.load({ payload: spec }).then(({ meta: { error }, data: newData }) => {
            if (!error) {
                setCurrentData((newData === null || newData === void 0 ? void 0 : newData.data) || []);
                updateInitialData((newData === null || newData === void 0 ? void 0 : newData.data) || []);
            }
        });
    }, []);
    const loading = getPolicesResult.meta.loading || sendChangesResult.meta.loading;
    const columns = helpers_2.getColumns(t);
    const active = selected[0];
    const clickArrow = (direction) => {
        setCurrentData((data) => {
            return data
                .map((policy) => {
                return Object.assign(Object.assign({}, policy), { priority: helpers_2.getPriority({
                        direction: direction,
                        priority: policy.priority,
                        activePriority: active.priority,
                        isActivePolicy: policy.id === active.id,
                    }) });
            })
                .sort(helpers_1.compareBy('priority'));
        });
        setSelected((currentSelect) => currentSelect[0].id === active.id
            ? [
                Object.assign(Object.assign({}, currentSelect[0]), { priority: direction === types_1.ARROW_DIRECTION.UP ? active.priority - 1 : active.priority + 1 }),
            ]
            : currentSelect);
    };
    const handleUp = () => {
        clickArrow(types_1.ARROW_DIRECTION.UP);
    };
    const handleDown = () => {
        clickArrow(types_1.ARROW_DIRECTION.DOWN);
    };
    const handleApplyButton = () => {
        sendChangesActions
            .load({
            payload: {
                priorities: currentData.map(({ id, priority }) => ({
                    policyId: id,
                    priority,
                })),
            },
        })
            .then(({ meta: { error }, }) => {
            if (!error) {
                deactivate();
                onDone === null || onDone === void 0 ? void 0 : onDone();
            }
        });
    };
    const buttonsDisabled = !active;
    const minPriority = lodash_maxby_1.default(currentData, 'priority') ? (_a = lodash_maxby_1.default(currentData, 'priority')) === null || _a === void 0 ? void 0 : _a.priority : 0;
    const upDisabled = buttonsDisabled ||
        ((_b = currentData.find((policy) => policy.id === active.id)) === null || _b === void 0 ? void 0 : _b.priority) === 1;
    const downDisabled = buttonsDisabled || minPriority === active.priority;
    const buttonUp = (react_1.default.createElement(components_1.Button, { ui: components_1.BUTTON_VIEW.text, onClick: handleUp, disabled: upDisabled },
        react_1.default.createElement(components_3.UpIcon, { disabled: upDisabled }),
        t('priorityOrderDialog|toolbar|button|up')));
    const buttonDown = (react_1.default.createElement(components_1.Button, { ui: components_1.BUTTON_VIEW.text, onClick: handleDown, disabled: downDisabled },
        react_1.default.createElement(components_3.DownIcon, { disabled: downDisabled }),
        t('priorityOrderDialog|toolbar|button|down')));
    return (react_1.default.createElement(components_1.Dialog, { hasTopPadding: false, header: t('priorityOrderDialog|header'), size: {
            width: components_1.DIALOG_SIZE.m,
            height: components_1.DIALOG_SIZE.xl,
        }, actions: [
            {
                view: components_1.ACTION_VIEW.primary,
                text: t('priorityOrderDialog|actions|apply'),
                disabled: lodash_isequal_1.default(initialData.map((i) => i.id), currentData.map((i) => i.id)),
                onClick: handleApplyButton,
            },
            {
                view: components_1.ACTION_VIEW.secondary,
                text: t('priorityOrderDialog|actions|cancel'),
                onClick: deactivate,
            },
        ], onRequestClose: () => {
            deactivate();
        } },
        react_1.default.createElement("div", { style: { display: 'flex', height: '100%', flexDirection: 'column' } },
            react_1.default.createElement("div", { style: { display: 'flex', flexGrow: 1, flexDirection: 'column' } },
                react_1.default.createElement(components_2.Grid, { idKey: ID_KEY, data: currentData, loading: loading, selectionMod: components_2.GRID_SELECTION_MOD.single, columns: columns, selected: selected.map(({ [ID_KEY]: id }) => id), onSelect: (selectedItems) => {
                        setSelected(selectedItems);
                    }, toolbar: [
                        {
                            type: components_1.TOOLBAR_ITEM_TYPE.customControl,
                            render: () => buttonUp,
                        },
                        {
                            type: components_1.TOOLBAR_ITEM_TYPE.customControl,
                            render: () => buttonDown,
                        },
                    ] })))));
};
exports.PolicyPriorityOrderDialog = PolicyPriorityOrderDialog;
exports.PolicyPriorityOrderDialog.displayName = 'PolicyPriorityOrderDialog';
//# sourceMappingURL=PolicyPriorityOrderDialog.js.map